import React, { useState, useEffect } from "react";
import { loadExcelData, convertToLaborUnitCosts, LaborMaterialItem } from "../utils/excelLoader";
import { loadNYCCostData, convertNYCToLaborUnitCosts, NYCRenovationTier } from "../utils/nycCostLoader";

interface FormData {
  type: string;
  property: string;
  zip: string;
  size: string;
  wallHeight: string;
  finish: string;
  scope: string[];
  cabinetLinearFeet: string;
  estimateType: 'Average' | 'Low' | 'High'; // New field
}

interface CostBreakdown {
  baseCost: number;
  scopeAdjustments: number;
  locationMultiplier: number;
  locationRegion: string;
  locationDescription: string;
  locationLowFactor: number;
  locationHighFactor: number;
  sizeScaleFactor: number;
  sizeScaleDescription: string;
  totalLow: number;
  totalHigh: number;
  wallArea: number;
  floorArea: number;
  laborCost: number;
  materialCost: number;
}

const RENOVATION_TYPES = {
  Kitchen: { 
    baseMultiplier: 1.2,
    wallAreaMultiplier: 0.7,
    floorAreaMultiplier: 1.0,
    laborRatio: 0.4,
    ikeaBaseCost: 200, // Base cost per linear foot for IKEA cabinets
    cabinetAreaRatio: 0.6 // 60% of wall area typically covered by cabinets
  },
  Bathroom: { 
    baseMultiplier: 1.1,
    wallAreaMultiplier: 0.8, // Bathroom walls often have fixtures and tiles
    floorAreaMultiplier: 1.0,
    laborRatio: 0.45 // 45% labor, 55% materials
  },
  "Whole Interior": { 
    baseMultiplier: 1.0,
    wallAreaMultiplier: 1.0,
    floorAreaMultiplier: 1.0,
    laborRatio: 0.35 // 35% labor, 65% materials
  },
  Flooring: { 
    baseMultiplier: 0.8,
    wallAreaMultiplier: 0.0, // Flooring doesn't use wall area
    floorAreaMultiplier: 1.0,
    laborRatio: 0.3 // 30% labor, 70% materials
  },
  Painting: { 
    baseMultiplier: 0.6,
    wallAreaMultiplier: 1.0,
    floorAreaMultiplier: 0.0, // Painting doesn't use floor area
    laborRatio: 0.5 // 50% labor, 50% materials
  },
  Other: { 
    baseMultiplier: 1.0,
    wallAreaMultiplier: 0.5, // Default to half wall area for other types
    floorAreaMultiplier: 1.0,
    laborRatio: 0.4 // 40% labor, 60% materials
  },
} as const;

const FINISH_LEVELS = {
  Budget: { 
    multiplier: 1.0, 
    baseCostPerSF: 100,
    ikeaMultiplier: 1.0 // Standard IKEA cabinets
  },
  "Mid-range": { 
    multiplier: 1.5, 
    baseCostPerSF: 200,
    ikeaMultiplier: 1.3 // IKEA cabinets with upgrades
  },
  "High-end / Luxury": { 
    multiplier: 2.5, 
    baseCostPerSF: 350,
    ikeaMultiplier: 1.8 // IKEA cabinets with premium upgrades
  },
} as const;

const SCOPE_ITEMS = {
  Demolition: { 
    costMultiplier: 0.15,
    baseCostPerSF: 6, // NYC: $2-$10/SF, using $6 average
    description: "Interior demolition, carting, removal of existing cabinetry, wall removal, electrical outlets, plumbing lines, and lights."
  },
  Plumbing: { 
    costMultiplier: 0.25,
    baseCostPerSF: 40, // NYC: $28-$50/SF, using $40 average
    description: "Water lines, sink relocation, gas line modifications, wet rooms, risers, vent stacks, and rough-ins."
  },
  Electrical: { 
    costMultiplier: 0.20,
    baseCostPerSF: 29, // NYC: $20-$33/SF, using $29 average for rough & fixtures
    description: "Electrical rough-in, fixtures, BX cable, LED lighting, panel upgrades, and 200-amp service."
  },
  Millwork: { 
    costMultiplier: 0.20,
    baseCostPerSF: 28, // NYC: $22-$34/SF for semi-custom maple cabinetry
    description: "Cabinetry and built-ins, including custom or semi-custom options with appliance integration."
  },
  "Appliance Install": { 
    costMultiplier: 0.10,
    baseCostPerSF: 15, // Estimated for appliance installation labor in NYC
    description: "Installation of new appliances: refrigerator, range, dishwasher, range hood, and other kitchen equipment."
  },
  "Permits/Design": { 
    costMultiplier: 0.15,
    baseCostPerSF: 16, // NYC: $10-$14/SF permits + $13-$19/SF design = $23-$33/SF, using $16 average
    description: "DOB filings, plan examinations, inspections, design planning, project management, and expediting fees."
  },
  Finishes: {
    costMultiplier: 0.18,
    baseCostPerSF: 14, // NYC: $10-$19/SF for painting & finishes, using $14 average
    description: "Painting, trim work, baseboards, crown molding, wall finishes, Level 5 skim coating, and low-VOC paints."
  }
} as const;

// Square footage scaling factors - 1,000 SF is baseline (1.0)
const SIZE_SCALING_FACTORS = {
  getScaleFactor: (squareFeet: number): { factor: number; description: string } => {
    if (squareFeet < 500) {
      return { 
        factor: 1.35, 
        description: "< 500 SF: Very high fixed costs; specialty crews still required; inefficiency in staging + demo" 
      };
    } else if (squareFeet < 700) {
      return { 
        factor: 1.175, 
        description: "500-700 SF: High overhead; micro-renovation inefficiencies" 
      };
    } else if (squareFeet < 1000) {
      return { 
        factor: 1.075, 
        description: "700-1,000 SF: Slightly more efficient than micro-reno, but still overhead-heavy" 
      };
    } else if (squareFeet < 1500) {
      return { 
        factor: 1.00, 
        description: "1,000-1,500 SF: Standard reference point (Baseline)" 
      };
    } else if (squareFeet < 2000) {
      return { 
        factor: 0.925, 
        description: "1,500-2,000 SF: More efficient crew utilization; some economies of scale kick in" 
      };
    } else if (squareFeet < 3000) {
      return { 
        factor: 0.825, 
        description: "2,000-3,000 SF: Overheads diluted; purchasing efficiency in materials" 
      };
    } else {
      return { 
        factor: 0.725, 
        description: "3,000+ SF: Major economies of scale; staging, labor distribution, bulk purchasing optimized" 
      };
    }
  }
} as const;

// Location-based pricing factors - NYC is baseline (1.0), all other regions relative to NYC
const LOCATION_FACTORS = {
  // NYC Metro Area (BASELINE)
  "10000-10999": { region: "NYC Metro", factor: 1.00, lowFactor: 0.95, highFactor: 1.05, description: "New York City (Baseline)" },
  "11000-11999": { region: "NYC Metro", factor: 1.00, lowFactor: 0.95, highFactor: 1.05, description: "NYC Metro (Baseline)" },
  "07000-07999": { region: "NYC Metro", factor: 1.00, lowFactor: 0.95, highFactor: 1.05, description: "Northern NJ (NYC Metro)" },
  "06000-06999": { region: "NYC Metro", factor: 1.00, lowFactor: 0.95, highFactor: 1.05, description: "CT (NYC Metro)" },
  
  // Boston Metro Area - 0.93 relative to NYC
  "02000-02999": { region: "Boston", factor: 0.93, lowFactor: 0.88, highFactor: 0.98, description: "Boston Metro (93% of NYC)" },
  
  // Philadelphia Metro Area - 0.83 relative to NYC
  "19000-19999": { region: "Philadelphia", factor: 0.83, lowFactor: 0.78, highFactor: 0.88, description: "Philadelphia Metro (83% of NYC)" },
  
  // Rest of Northeast
  "01000-01999": { region: "Northeast", factor: 0.85, lowFactor: 0.80, highFactor: 0.90, description: "Massachusetts (excl. Boston)" },
  "03000-03999": { region: "Northeast", factor: 0.75, lowFactor: 0.70, highFactor: 0.80, description: "New Hampshire" },
  "04000-04999": { region: "Northeast", factor: 0.70, lowFactor: 0.65, highFactor: 0.75, description: "Maine" },
  "05000-05999": { region: "Northeast", factor: 0.75, lowFactor: 0.70, highFactor: 0.80, description: "Vermont" },
  "08000-08999": { region: "Northeast", factor: 0.75, lowFactor: 0.70, highFactor: 0.80, description: "New Jersey (rest)" },
  "09000-09999": { region: "Northeast", factor: 0.70, lowFactor: 0.65, highFactor: 0.75, description: "Armed Forces" },
  "12000-14999": { region: "Northeast", factor: 0.80, lowFactor: 0.75, highFactor: 0.85, description: "Upstate NY" },
  "15000-18999": { region: "Mid-Atlantic", factor: 0.75, lowFactor: 0.70, highFactor: 0.80, description: "Pennsylvania (rest)" },
  
  // Mid-Atlantic
  "20000-20999": { region: "Mid-Atlantic", factor: 0.78, lowFactor: 0.73, highFactor: 0.83, description: "DC Metro" },
  "21000-21999": { region: "Mid-Atlantic", factor: 0.75, lowFactor: 0.70, highFactor: 0.80, description: "Maryland" },
  "22000-24699": { region: "Mid-Atlantic", factor: 0.75, lowFactor: 0.70, highFactor: 0.80, description: "Virginia" },
  "24700-26999": { region: "Mid-Atlantic", factor: 0.68, lowFactor: 0.63, highFactor: 0.73, description: "West Virginia" },
  
  // Southeast
  "27000-28999": { region: "North Carolina", factor: 0.64, lowFactor: 0.60, highFactor: 0.68, description: "North Carolina" },
  "29000-29999": { region: "South Carolina", factor: 0.60, lowFactor: 0.56, highFactor: 0.64, description: "South Carolina" },
  "30000-31999": { region: "Atlanta", factor: 0.64, lowFactor: 0.60, highFactor: 0.68, description: "Atlanta Metro" },
  "32000-34999": { region: "Southeast", factor: 0.70, lowFactor: 0.65, highFactor: 0.75, description: "Florida" },
  "35000-36999": { region: "Southeast", factor: 0.60, lowFactor: 0.56, highFactor: 0.64, description: "Alabama" },
  "37000-37299": { region: "Nashville", factor: 0.68, lowFactor: 0.64, highFactor: 0.72, description: "Nashville, TN" },
  "37300-38999": { region: "Tennessee", factor: 0.60, lowFactor: 0.56, highFactor: 0.64, description: "Tennessee (rest)" },
  "38600-39999": { region: "Southeast", factor: 0.58, lowFactor: 0.54, highFactor: 0.62, description: "Mississippi" },
  "39000-39999": { region: "Southeast", factor: 0.58, lowFactor: 0.54, highFactor: 0.62, description: "Louisiana (rest)" },
  "70000-70199": { region: "Southeast", factor: 0.65, lowFactor: 0.60, highFactor: 0.70, description: "New Orleans" },
  
  // Midwest - Large Cities
  "60000-61999": { region: "Midwest", factor: 0.72, lowFactor: 0.68, highFactor: 0.76, description: "Chicago Metro" },
  "55000-55999": { region: "Midwest", factor: 0.72, lowFactor: 0.68, highFactor: 0.76, description: "Minneapolis Metro" },
  
  // Midwest - Smaller Cities
  "40000-41999": { region: "Midwest", factor: 0.60, lowFactor: 0.56, highFactor: 0.64, description: "Kentucky" },
  "43000-45999": { region: "Midwest", factor: 0.64, lowFactor: 0.60, highFactor: 0.68, description: "Ohio" },
  "46000-47999": { region: "Midwest", factor: 0.60, lowFactor: 0.56, highFactor: 0.64, description: "Indiana" },
  "48000-49999": { region: "Midwest", factor: 0.62, lowFactor: 0.58, highFactor: 0.66, description: "Michigan" },
  "50000-52999": { region: "Midwest", factor: 0.60, lowFactor: 0.56, highFactor: 0.64, description: "Iowa" },
  "53000-54999": { region: "Midwest", factor: 0.60, lowFactor: 0.56, highFactor: 0.64, description: "Wisconsin" },
  "56000-56999": { region: "Midwest", factor: 0.60, lowFactor: 0.56, highFactor: 0.64, description: "Minnesota (rest)" },
  "57000-57999": { region: "Midwest", factor: 0.58, lowFactor: 0.54, highFactor: 0.62, description: "South Dakota" },
  "58000-58999": { region: "Midwest", factor: 0.58, lowFactor: 0.54, highFactor: 0.62, description: "North Dakota" },
  "59000-59999": { region: "Midwest", factor: 0.58, lowFactor: 0.54, highFactor: 0.62, description: "Montana" },
  "62000-62999": { region: "Midwest", factor: 0.60, lowFactor: 0.56, highFactor: 0.64, description: "Illinois (rest)" },
  "63000-65999": { region: "Midwest", factor: 0.60, lowFactor: 0.56, highFactor: 0.64, description: "Missouri" },
  "66000-67999": { region: "Midwest", factor: 0.58, lowFactor: 0.54, highFactor: 0.62, description: "Kansas" },
  "68000-69999": { region: "Midwest", factor: 0.58, lowFactor: 0.54, highFactor: 0.62, description: "Nebraska" },
  
  // South
  "70200-74999": { region: "South", factor: 0.60, lowFactor: 0.56, highFactor: 0.64, description: "Louisiana, Arkansas, Oklahoma" },
  
  // Southwest (Texas, Arizona, Nevada, NM)
  "75000-79999": { region: "Southwest", factor: 0.68, lowFactor: 0.64, highFactor: 0.72, description: "Texas" },
  "85000-86999": { region: "Southwest", factor: 0.68, lowFactor: 0.64, highFactor: 0.72, description: "Arizona" },
  "87000-88999": { region: "Southwest", factor: 0.68, lowFactor: 0.64, highFactor: 0.72, description: "New Mexico" },
  "89000-89999": { region: "Southwest", factor: 0.70, lowFactor: 0.65, highFactor: 0.75, description: "Nevada (Las Vegas)" },
  
  // Mountain West
  "80000-81999": { region: "Mountain", factor: 0.75, lowFactor: 0.70, highFactor: 0.80, description: "Colorado" },
  "82000-83999": { region: "Mountain", factor: 0.65, lowFactor: 0.60, highFactor: 0.70, description: "Wyoming, Idaho" },
  "84000-84999": { region: "Mountain", factor: 0.70, lowFactor: 0.65, highFactor: 0.75, description: "Utah" },
  
  // West Coast
  "90000-96999": { region: "California", factor: 0.90, lowFactor: 0.84, highFactor: 0.96, description: "California" },
  "97000-97999": { region: "Oregon", factor: 0.80, lowFactor: 0.75, highFactor: 0.85, description: "Oregon" },
  "98000-99999": { region: "Washington State", factor: 0.84, lowFactor: 0.79, highFactor: 0.89, description: "Washington State" }
} as const;

// Function to get location factor based on zip code and estimate type
const getLocationFactor = (zipCode: string, estimateType: 'Average' | 'Low' | 'High'): { factor: number; region: string; description: string; lowFactor: number; highFactor: number } => {
  const zip = parseInt(zipCode);
  
  // Find the appropriate range
  for (const [range, data] of Object.entries(LOCATION_FACTORS)) {
    const [min, max] = range.split('-').map(n => parseInt(n));
    if (zip >= min && zip <= max) {
      // Use the appropriate factor based on estimate type
      let factor: number = data.factor;
      if (estimateType === 'Low') {
        factor = data.lowFactor;
      } else if (estimateType === 'High') {
        factor = data.highFactor;
      }
      
      return {
        factor,
        region: data.region,
        description: data.description,
        lowFactor: data.lowFactor,
        highFactor: data.highFactor
      };
    }
  }
  
  // Default fallback
  return { 
    factor: 1.0, 
    region: "Unknown", 
    description: "Standard Rate",
    lowFactor: 0.9,
    highFactor: 1.1
  };
};

// New: Comprehensive Labor/Unit Cost Data for Beta 2
const LABOR_UNIT_COSTS = {
  // GENERAL CONSTRUCTION (90% Labor, 10% Material)
  "General Protection": { unit: "SF", price: 1.00, laborPercent: 90, materialPercent: 10 },
  "Laborer For Cleanup": { unit: "Man-Month", price: 16000.00, laborPercent: 100, materialPercent: 0 },
  "Elevator Sandby Cost Allowance": { unit: "Weeks", price: 500.00, laborPercent: 100, materialPercent: 0 },
  "Teamster Labor": { unit: "Weeks", price: 1.00, laborPercent: 100, materialPercent: 0 },
  "Local 1 & 14 Labor": { unit: "Weeks", price: 2.00, laborPercent: 100, materialPercent: 0 },
  "Scaffolding": { unit: "SF", price: 35.00, laborPercent: 85.7, materialPercent: 14.3 },
  "Sidewalk Bridge": { unit: "LF", price: 600.00, laborPercent: 91.7, materialPercent: 8.3 },
  "Independent Testing Agencies": { unit: "TD", price: 3000.00, laborPercent: 100, materialPercent: 0 },

  // DEMOLITION (90% Labor, 10% Material)
  "Gyp. Partition Walls": { unit: "LF", price: 10.00, laborPercent: 90, materialPercent: 10 },
  "Masonry Walls": { unit: "SF", price: 2.00, laborPercent: 90, materialPercent: 10 },
  "Millwork Cabinets": { unit: "LF", price: 25.00, laborPercent: 90, materialPercent: 10 },
  "Act Ceiling System": { unit: "SF", price: 0.75, laborPercent: 90, materialPercent: 10 },
  "Gyp. Ceiling System": { unit: "SF", price: 1.00, laborPercent: 90, materialPercent: 10 },
  "Remove Floor Finishes": { unit: "SF", price: 0.55, laborPercent: 90, materialPercent: 10 },
  "Ceramic & Stone Floor Removal": { unit: "SF", price: 1.25, laborPercent: 90, materialPercent: 10 },
  "Doors & Frames": { unit: "EA", price: 60.00, laborPercent: 90, materialPercent: 10 },
  "Slab Removal": { unit: "SF", price: 7.00, laborPercent: 90, materialPercent: 10 },
  "Saw Cut & Slab Removal": { unit: "SF", price: 25.00, laborPercent: 90, materialPercent: 10 },
  "Beam Pockets": { unit: "EA", price: 250.00, laborPercent: 90, materialPercent: 10 },
  "Cart MEP": { unit: "SF", price: 0.75, laborPercent: 90, materialPercent: 10 },

  // DRYWALL & ACOUSTICAL CEILINGS (70% Labor, 30% Material)
  "1 Hour Rated Partition": { unit: "SF", price: 10.00, laborPercent: 70, materialPercent: 30 },
  "2 Hour Rated Partition": { unit: "SF", price: 12.00, laborPercent: 70, materialPercent: 30 },
  "Shaft Wall": { unit: "SF", price: 15.00, laborPercent: 70, materialPercent: 30 },
  "Furr/Laminate Existing Wall": { unit: "SF", price: 7.00, laborPercent: 70, materialPercent: 30 },
  "Dwarf Partition": { unit: "SF", price: 12.00, laborPercent: 70, materialPercent: 30 },
  "Framed Opening": { unit: "EA", price: 200.00, laborPercent: 70, materialPercent: 30 },
  "Trimmed Opening": { unit: "EA", price: 400.00, laborPercent: 70, materialPercent: 30 },
  "2' X 2' A.C.T.": { unit: "SF", price: 9.00, laborPercent: 70, materialPercent: 30 },
  "2' X 4' A.C.T.": { unit: "SF", price: 8.00, laborPercent: 70, materialPercent: 30 },
  "1' X 1' Concealed Spline": { unit: "SF", price: 12.00, laborPercent: 70, materialPercent: 30 },
  "S/R Ceiling": { unit: "SF", price: 12.00, laborPercent: 70, materialPercent: 30 },

  // PAINTING (75% Labor, 25% Material)
  "Paint S/R Walls": { unit: "SF", price: 0.65, laborPercent: 75, materialPercent: 25 },
  "Paint Temporary Walls": { unit: "SF", price: 1.00, laborPercent: 75, materialPercent: 25 },
  "Paint S/R Ceilings": { unit: "SF", price: 1.00, laborPercent: 75, materialPercent: 25 },
  "GWB Soffit/Fascias": { unit: "LF", price: 3.00, laborPercent: 75, materialPercent: 25 },
  "Paint Interior/Exterior Columns": { unit: "EA", price: 100.00, laborPercent: 75, materialPercent: 25 },
  "Paint New HM Door & Frame": { unit: "EA", price: 125.00, laborPercent: 75, materialPercent: 25 },
  "Paint Existing HM Door & Frame": { unit: "EA", price: 125.00, laborPercent: 75, materialPercent: 25 },
  "Skimcoat Existing Walls": { unit: "SF", price: 1.50, laborPercent: 75, materialPercent: 25 },
  "Strip & Paint Existing Windows": { unit: "EA", price: 500.00, laborPercent: 75, materialPercent: 25 },
  "Strip & Paint Existing Doors": { unit: "EA", price: 750.00, laborPercent: 75, materialPercent: 25 },

  // FLOOR COVERING (25% Labor, 75% Material)
  "Carpet - Low Grade": { unit: "SY", price: 35.00, laborPercent: 25, materialPercent: 75 },
  "Carpet - Mid Grade": { unit: "SY", price: 50.00, laborPercent: 25, materialPercent: 75 },
  "Carpet - High Grade": { unit: "SY", price: 65.00, laborPercent: 25, materialPercent: 75 },
  "VCT": { unit: "SF", price: 6.00, laborPercent: 25, materialPercent: 75 },
  "Fritz Tile": { unit: "SF", price: 15.00, laborPercent: 25, materialPercent: 75 },
  "Transition Strip": { unit: "EA", price: 50.00, laborPercent: 25, materialPercent: 75 },
  "Vinyl Base": { unit: "LF", price: 2.50, laborPercent: 25, materialPercent: 75 },
  "Basic Floor Prep": { unit: "SF", price: 1.00, laborPercent: 25, materialPercent: 75 },

  // WOOD FLOORING (60% Labor, 40% Material)
  "Strip Oak Wood Flooring": { unit: "SF", price: 30.00, laborPercent: 60, materialPercent: 40 },
  "Refinish Existing Wood Flooring": { unit: "SF", price: 7.00, laborPercent: 60, materialPercent: 40 },

  // CERAMIC TILE (60% Labor, 40% Material)
  "Ceramic Wall Tile": { unit: "SF", price: 15.00, laborPercent: 60, materialPercent: 40 },
  "Ceramic Floor Tile": { unit: "SF", price: 15.00, laborPercent: 60, materialPercent: 40 },
  "Porcelain Wall Tile": { unit: "SF", price: 20.00, laborPercent: 60, materialPercent: 40 },
  "Porcelain Floor Tile": { unit: "SF", price: 20.00, laborPercent: 60, materialPercent: 40 },
  "Glass Tile - Mid Grade": { unit: "SF", price: 50.00, laborPercent: 60, materialPercent: 40 },
  "Cove Tile Base": { unit: "LF", price: 20.00, laborPercent: 60, materialPercent: 40 },
  "Waterproofing Membrane": { unit: "SF", price: 5.00, laborPercent: 60, materialPercent: 40 },

  // FINISH CARPENTRY / MILLWORK (55% Labor, 45% Material)
  "Veneer Finish Doors (3'x7')": { unit: "EA", price: 1400.00, laborPercent: 55, materialPercent: 45 },
  "Veneer Finish Doors (3'x8')": { unit: "EA", price: 1500.00, laborPercent: 55, materialPercent: 45 },
  "Paint Grade Wood Door (3'x7')": { unit: "EA", price: 900.00, laborPercent: 55, materialPercent: 45 },
  "Paint Grade Wood Doors (3'x8')": { unit: "EA", price: 1000.00, laborPercent: 55, materialPercent: 45 },
  "Veneer Frames (3'x7')": { unit: "EA", price: 1800.00, laborPercent: 55, materialPercent: 45 },
  "Veneer Frames (3'x8')": { unit: "EA", price: 2000.00, laborPercent: 55, materialPercent: 45 },
  "Paint Grade Frames (3'x7')": { unit: "EA", price: 1200.00, laborPercent: 55, materialPercent: 45 },
  "Paint Grade Frames (3'x8')": { unit: "EA", price: 1300.00, laborPercent: 55, materialPercent: 45 },
  "P-Lam Upper Cabinets": { unit: "LF", price: 350.00, laborPercent: 55, materialPercent: 45 },
  "P-Lam Lower Cabinets": { unit: "LF", price: 450.00, laborPercent: 55, materialPercent: 45 },
  "P-Lam Counter Tops": { unit: "SF", price: 45.00, laborPercent: 55, materialPercent: 45 },
  "P-Lam Counter Work Surface": { unit: "SF", price: 50.00, laborPercent: 55, materialPercent: 45 },
  "Storage Shelves": { unit: "LF", price: 50.00, laborPercent: 55, materialPercent: 45 },
  "Wood Wall Paneling": { unit: "SF", price: 85.00, laborPercent: 55, materialPercent: 45 },
  "Wood Base (Paint Grade)": { unit: "LF", price: 30.00, laborPercent: 55, materialPercent: 45 },
  "Crown Molding (Paint Grade)": { unit: "LF", price: 60.00, laborPercent: 55, materialPercent: 45 },

  // PLUMBING (65% Labor, 35% Material)
  "Typical Lavatory": { unit: "EA", price: 2200.00, laborPercent: 65, materialPercent: 35 },
  "Typical Electronic Lavatory": { unit: "EA", price: 2600.00, laborPercent: 65, materialPercent: 35 },
  "Typical ADA Lavatory": { unit: "EA", price: 2300.00, laborPercent: 65, materialPercent: 35 },
  "Typical Water Closet": { unit: "EA", price: 2400.00, laborPercent: 65, materialPercent: 35 },
  "Typical Electronic Water Closet": { unit: "EA", price: 2800.00, laborPercent: 65, materialPercent: 35 },
  "Typical Urinal": { unit: "EA", price: 2200.00, laborPercent: 65, materialPercent: 35 },
  "Shower Body With Lead Pan": { unit: "EA", price: 2750.00, laborPercent: 65, materialPercent: 35 },
  "3\" Floor Drain": { unit: "EA", price: 2000.00, laborPercent: 65, materialPercent: 35 },
  "4\" Floor Sink": { unit: "EA", price: 2500.00, laborPercent: 65, materialPercent: 35 },
  "5 Gallon Water Heater": { unit: "EA", price: 2300.00, laborPercent: 65, materialPercent: 35 },
  "10 Gallon Water Heater": { unit: "EA", price: 2500.00, laborPercent: 65, materialPercent: 35 },
  "30 Gallon Water Heater": { unit: "EA", price: 3600.00, laborPercent: 65, materialPercent: 35 },
  "40 Gallon Water Heater": { unit: "EA", price: 4900.00, laborPercent: 65, materialPercent: 35 },
  "52 Gallon Water Heater": { unit: "EA", price: 8500.00, laborPercent: 65, materialPercent: 35 },

  // HVAC (60% Labor, 40% Material)
  "VAV Box W/Tstat Up To 500 cfm": { unit: "EA", price: 1400.00, laborPercent: 60, materialPercent: 40 },
  "VAV Box W/Tstat 501 To 1000 cfm": { unit: "EA", price: 1600.00, laborPercent: 60, materialPercent: 40 },
  "VAV Box W/Tstat 1001 To 1500 cfm": { unit: "EA", price: 1635.00, laborPercent: 60, materialPercent: 40 },
  "Fan Powered VAV Box Up To 500 cfm": { unit: "EA", price: 2300.00, laborPercent: 60, materialPercent: 40 },
  "Exhaust Fan (Penn Zephyr Type) Z-8 To Z-12": { unit: "EA", price: 1450.00, laborPercent: 60, materialPercent: 40 },
  "Exhaust Fan (Penn Zephyr Type) Z-14": { unit: "EA", price: 1650.00, laborPercent: 60, materialPercent: 40 },
  "Exhaust Fan (Penn Zephyr Type) Z-16": { unit: "EA", price: 2200.00, laborPercent: 60, materialPercent: 40 },
  "Install CRAC Units (Ton)": { unit: "EA", price: 4500.00, laborPercent: 60, materialPercent: 40 },
  "Install Supplemental A/C (Ton)": { unit: "EA", price: 7500.00, laborPercent: 60, materialPercent: 40 },
  "Ductwork Medium Pressure - Sealed": { unit: "LB", price: 9.00, laborPercent: 60, materialPercent: 40 },
  "Ductwork Low Pressure -Rectagular": { unit: "LB", price: 8.75, laborPercent: 60, materialPercent: 40 },
  "Ductwork Medium Pressure - Round Spiral": { unit: "LB", price: 10.20, laborPercent: 60, materialPercent: 40 },
  "Ductwork 6\" & 8\" Diameter Flexible": { unit: "LF", price: 50.00, laborPercent: 60, materialPercent: 40 },
  "Ductwork Acoustical Lining 1\" - 1 1/2 lb. Density": { unit: "SF", price: 3.95, laborPercent: 60, materialPercent: 40 },
  "Fire Damper With Fusible Link": { unit: "SF", price: 165.00, laborPercent: 60, materialPercent: 40 },
  "Combination Fire/Smoke Damper": { unit: "SF", price: 450.00, laborPercent: 60, materialPercent: 40 },
  "Air Outlets 12 x 12 Face With 6\" Neck": { unit: "EA", price: 220.00, laborPercent: 60, materialPercent: 40 },
  "Air Outlets 24 x 24 Face With 8\" Neck": { unit: "EA", price: 235.00, laborPercent: 60, materialPercent: 40 },
  "Registers Up To 11 x 11": { unit: "EA", price: 220.00, laborPercent: 60, materialPercent: 40 },
  "Ceiling Grilles Up To 11 x 11": { unit: "EA", price: 145.00, laborPercent: 60, materialPercent: 40 },

  // ELECTRICAL (65% Labor, 35% Material)
  "Transformer - 6 kva": { unit: "EA", price: 4800.00, laborPercent: 65, materialPercent: 35 },
  "Transformer - 9 kva": { unit: "EA", price: 5400.00, laborPercent: 65, materialPercent: 35 },
  "Transformer - 15 kva": { unit: "EA", price: 6200.00, laborPercent: 65, materialPercent: 35 },
  "Transformer - 30 kva": { unit: "EA", price: 7900.00, laborPercent: 65, materialPercent: 35 },
  "Panel 208/120V Main Lugs Only12 CKTS, 100 Amperes": { unit: "EA", price: 4700.00, laborPercent: 65, materialPercent: 35 },
  "Panel 208/120V Main Lugs Only18 CKTS, 100 Amperes": { unit: "EA", price: 4900.00, laborPercent: 65, materialPercent: 35 },
  "Panel 208/120V Main Lugs Only 24 CKTS, 100 Amperes": { unit: "EA", price: 5300.00, laborPercent: 65, materialPercent: 35 },
  "EMT Conduit With Set Screw Fittings/Suppports 3/4\"": { unit: "LF", price: 12.97, laborPercent: 65, materialPercent: 35 },
  "EMT Conduit With Set Screw Fittings/Suppports 1\"": { unit: "LF", price: 15.74, laborPercent: 65, materialPercent: 35 },
  "EMT Conduit With Set Screw Fittings/Suppports 1 1/4\"": { unit: "LF", price: 23.36, laborPercent: 65, materialPercent: 35 },
  "Junction Box 6\" x 6\" x 4\"": { unit: "EA", price: 220.00, laborPercent: 65, materialPercent: 35 },
  "Junction Box 8\" x 8\" x 4\"": { unit: "EA", price: 250.00, laborPercent: 65, materialPercent: 35 },
  "Junction Box 12\" x 12\" x 6\"": { unit: "EA", price: 560.00, laborPercent: 65, materialPercent: 35 },
  "#14 THHN Wire": { unit: "LF", price: 1.11, laborPercent: 65, materialPercent: 35 },
  "#12 THHN Wire": { unit: "LF", price: 1.33, laborPercent: 65, materialPercent: 35 },
  "#10 THHN Wire": { unit: "LF", price: 1.73, laborPercent: 65, materialPercent: 35 },
  "#8 THHN Wire": { unit: "LF", price: 2.42, laborPercent: 65, materialPercent: 35 },
  "#6 THHN Wire": { unit: "LF", price: 2.95, laborPercent: 65, materialPercent: 35 },
  "Install Only 1' x 4' Recessed Fluorescent Light Fixture - 2 Lamp": { unit: "EA", price: 400.00, laborPercent: 100, materialPercent: 0 },
  "Install Only 2' x 4' Recessed Fluorescent Light Fixture - 2 Lamp": { unit: "EA", price: 425.00, laborPercent: 100, materialPercent: 0 },
  "Install Only 2' x 2' Recessed Fluorescent Light Fixture - 2 Lamp": { unit: "EA", price: 400.00, laborPercent: 100, materialPercent: 0 },
  "Light Switch": { unit: "EA", price: 400.00, laborPercent: 65, materialPercent: 35 },
  "3 way Light Switch": { unit: "EA", price: 450.00, laborPercent: 65, materialPercent: 35 },
  "4 way Light Switch": { unit: "EA", price: 500.00, laborPercent: 65, materialPercent: 35 },
  "Dimmer Switch - 600 Watt": { unit: "EA", price: 600.00, laborPercent: 65, materialPercent: 35 },
  "15 Amp Duplex Receptacle": { unit: "EA", price: 560.00, laborPercent: 65, materialPercent: 35 },
  "15 Amp Quad Receptacle": { unit: "EA", price: 620.00, laborPercent: 65, materialPercent: 35 },
  "Ground Fault (GFI) Receptacle": { unit: "EA", price: 640.00, laborPercent: 65, materialPercent: 35 },
  "Dedicated Duplex Receptacle": { unit: "EA", price: 930.00, laborPercent: 65, materialPercent: 35 },
  "Wall Mounted Occupancy Sensor": { unit: "EA", price: 575.00, laborPercent: 65, materialPercent: 35 },
  "Ceiling Mounted Occupancy Sensor W/Pack": { unit: "EA", price: 700.00, laborPercent: 65, materialPercent: 35 },
  "Fire Alarm Strobe": { unit: "EA", price: 1000.00, laborPercent: 65, materialPercent: 35 },
  "Fire Alarm Combination Speaker/Strobe": { unit: "EA", price: 1500.00, laborPercent: 65, materialPercent: 35 },
  "Fire Alarm Speaker": { unit: "EA", price: 1000.00, laborPercent: 65, materialPercent: 35 },
  "Fire Alarm Pull Station": { unit: "EA", price: 1200.00, laborPercent: 65, materialPercent: 35 },
  "Fire Alarm Smoke Detector W/Programming": { unit: "EA", price: 1850.00, laborPercent: 65, materialPercent: 35 },
  "Fire Alarm Duct Smoke Detector W/Programming": { unit: "EA", price: 2050.00, laborPercent: 65, materialPercent: 35 },

  // FIRE PROTECTION (70% Labor, 30% Material)
  "Brass Upright Head": { unit: "EA", price: 280.00, laborPercent: 70, materialPercent: 30 },
  "Chrome Upright Head": { unit: "EA", price: 315.00, laborPercent: 70, materialPercent: 30 },
  "Concealed Head": { unit: "EA", price: 325.00, laborPercent: 70, materialPercent: 30 },
  "Sidewall Head": { unit: "EA", price: 330.00, laborPercent: 70, materialPercent: 30 },
  "Dry-pendent Head": { unit: "EA", price: 420.00, laborPercent: 70, materialPercent: 30 },
  "Black Piping 1\"": { unit: "LF", price: 26.00, laborPercent: 70, materialPercent: 30 },
  "Black Piping 1 1/4\"": { unit: "LF", price: 30.00, laborPercent: 70, materialPercent: 30 },
  "Black Piping 1 1/2\"": { unit: "LF", price: 33.00, laborPercent: 70, materialPercent: 30 },
  "Black Piping 2\"": { unit: "LF", price: 38.00, laborPercent: 70, materialPercent: 30 },
  "Check Valve 2\"": { unit: "EA", price: 710.00, laborPercent: 70, materialPercent: 30 },
  "Check Valve 2-1/2\"": { unit: "EA", price: 810.00, laborPercent: 70, materialPercent: 30 },
  "Check Valve 3\"": { unit: "EA", price: 830.00, laborPercent: 70, materialPercent: 30 },
  "Control Valve 2\"": { unit: "EA", price: 2900.00, laborPercent: 70, materialPercent: 30 },
  "Control Valve 2-1/2\"": { unit: "EA", price: 3200.00, laborPercent: 70, materialPercent: 30 },
  "Control Valve 3\"": { unit: "EA", price: 3650.00, laborPercent: 70, materialPercent: 30 },
  "O, S &Y Valves 2\"": { unit: "EA", price: 900.00, laborPercent: 70, materialPercent: 30 },
  "O, S &Y Valves 2-1/2\"": { unit: "EA", price: 1125.00, laborPercent: 70, materialPercent: 30 },
  "O, S &Y Valves 3\"": { unit: "EA", price: 1625.00, laborPercent: 70, materialPercent: 30 },
  "Water Flow Switch": { unit: "EA", price: 630.00, laborPercent: 70, materialPercent: 30 },
  "Tamper Switch": { unit: "EA", price: 530.00, laborPercent: 70, materialPercent: 30 },
  "Fire Hose Cabinet w/125' Of Hose": { unit: "EA", price: 2900.00, laborPercent: 70, materialPercent: 30 },
  "Fire Hose Rack w/125' Of Hose": { unit: "EA", price: 1800.00, laborPercent: 70, materialPercent: 30 },
  "Head Guards": { unit: "EA", price: 45.00, laborPercent: 70, materialPercent: 30 },
  "Spare Sprinkler Cabinet With Heads": { unit: "EA", price: 500.00, laborPercent: 70, materialPercent: 30 },
  "Roof Manifold Assembly": { unit: "EA", price: 1500.00, laborPercent: 70, materialPercent: 30 },
  "Temporary Fire Loop": { unit: "LF", price: 150.00, laborPercent: 70, materialPercent: 30 },

  // SPECIALTIES (60% Labor, 40% Material)
  "Floor Mounted Baked Enamel Toilet Partitions": { unit: "EA", price: 1300.00, laborPercent: 60, materialPercent: 40 },
  "Ceiling Mounted Baked Enamel Toilet Partitions": { unit: "EA", price: 1600.00, laborPercent: 60, materialPercent: 40 },
  "Floor Mounted Stainless Steel Toilet Partitions": { unit: "EA", price: 1900.00, laborPercent: 60, materialPercent: 40 },
  "Ceiling Mounted Stainless Steel Toilet Partitions": { unit: "EA", price: 2500.00, laborPercent: 60, materialPercent: 40 },
  "Urinal Screens": { unit: "EA", price: 150.00, laborPercent: 60, materialPercent: 40 },
  "Furnish Toilet Seat Cover & Napkin Dispenser Combo": { unit: "EA", price: 440.00, laborPercent: 0, materialPercent: 100 },
  "Furnish Toilet Paper & Seat Cover Dispenser Combo": { unit: "EA", price: 440.00, laborPercent: 0, materialPercent: 100 },
  "Furnish Recessed Toilet Paper Dispenser & Waste Receptacle": { unit: "EA", price: 253.00, laborPercent: 0, materialPercent: 100 },
  "Furnish Semi-Recessed Toilet Paper Dispenser & Waste Receptacle": { unit: "EA", price: 352.00, laborPercent: 0, materialPercent: 100 },
  "Furnish Recessed Napkin / Tampon Vendor": { unit: "EA", price: 440.00, laborPercent: 0, materialPercent: 100 },
  "Furnish Grab Bar": { unit: "EA", price: 44.00, laborPercent: 0, materialPercent: 100 },
  "Furnish Coat Hooks": { unit: "EA", price: 15.00, laborPercent: 0, materialPercent: 100 },
  "Furnish Soap Dispenser": { unit: "EA", price: 50.00, laborPercent: 0, materialPercent: 100 },
  "Furnish Recessed Paper Towel Dispenser & Waste Receptacle": { unit: "EA", price: 198.00, laborPercent: 0, materialPercent: 100 },
  "Furnish Fire Extinguishers And Cabinets": { unit: "EA", price: 350.00, laborPercent: 0, materialPercent: 100 },
  "Furnish Recessed Electric Ceiling Mounted Projection Screen": { unit: "EA", price: 5000.00, laborPercent: 0, materialPercent: 100 },

  // WINDOW TREATMENTS (70% Labor, 30% Material)
  "Remove, Clean & Reinstall Existing Window Treatment": { unit: "EA", price: 100.00, laborPercent: 100, materialPercent: 0 },
  "Manual Window Roller Shade, Mecho Shades": { unit: "EA", price: 175.00, laborPercent: 70, materialPercent: 30 },
  "Mini Blinds-Manual": { unit: "EA", price: 120.00, laborPercent: 70, materialPercent: 30 },
  "Motorized Window Roller Shade - Blackout": { unit: "EA", price: 400.00, laborPercent: 70, materialPercent: 30 },
  "Motorized Window Roller Shade, Dual Roller": { unit: "EA", price: 1700.00, laborPercent: 70, materialPercent: 30 },

  // KITCHEN EQUIPMENT (15% Labor, 85% Material)
  "Three Compartment Walk In Refridgerator/Freezer Storage": { unit: "EA", price: 30000.00, laborPercent: 15, materialPercent: 85 },
  "Hand Sink & Utility Sink": { unit: "EA", price: 3000.00, laborPercent: 15, materialPercent: 85 },
  "Pot Sinks": { unit: "EA", price: 6250.00, laborPercent: 15, materialPercent: 85 },
  "20 Quart Mixer": { unit: "EA", price: 5000.00, laborPercent: 15, materialPercent: 85 },
  "Ice Maker": { unit: "EA", price: 4000.00, laborPercent: 15, materialPercent: 85 },
  "Ice Bin": { unit: "EA", price: 2250.00, laborPercent: 15, materialPercent: 85 },
  "Work Table": { unit: "EA", price: 3300.00, laborPercent: 15, materialPercent: 85 },
  "Water Filter": { unit: "EA", price: 900.00, laborPercent: 15, materialPercent: 85 },
  "Kettle, Tilting, 20 Quart": { unit: "EA", price: 16500.00, laborPercent: 15, materialPercent: 85 },
  "Exhaust Ventilator": { unit: "EA", price: 8250.00, laborPercent: 15, materialPercent: 85 },
  "Griddle": { unit: "EA", price: 6750.00, laborPercent: 15, materialPercent: 85 },
  "Combi Oven": { unit: "EA", price: 23000.00, laborPercent: 15, materialPercent: 85 },
  "Display Refriderator": { unit: "EA", price: 16000.00, laborPercent: 15, materialPercent: 85 },
  "Delhi Counter": { unit: "EA", price: 11000.00, laborPercent: 15, materialPercent: 85 },
  "Slicer": { unit: "EA", price: 4500.00, laborPercent: 15, materialPercent: 85 },

  // ELEVATORS (100% Labor, 0% Material)
  "Relocate Call Button / Lantern": { unit: "LS", price: 15000.00, laborPercent: 100, materialPercent: 0 },

  // TELECOMMUNICATIONS (65% Labor, 35% Material)
  "Telecommunications Cabling (4 Cables per Drop) Based on 200 LF": { unit: "Drop", price: 1200.00, laborPercent: 65, materialPercent: 35 },
  "Cable Tray - 12\" Standard": { unit: "LF", price: 150.00, laborPercent: 65, materialPercent: 35 },

  // TEMPORARY POWER & LIGHT
  "Temporary Light And Power": { unit: "SF area", price: 1.00, laborPercent: 65, materialPercent: 35 }
} as const;

export default function RenovationEstimator() {
  const [formData, setFormData] = useState<FormData>({
    type: "",
    property: "",
    zip: "",
    size: "",
    wallHeight: "8", // Default to standard 8-foot ceiling
    finish: "",
    scope: [],
    cabinetLinearFeet: "", // Initialize new field
    estimateType: 'Average', // Default to Average
  });
  const [costBreakdown, setCostBreakdown] = useState<CostBreakdown | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [excelData, setExcelData] = useState<LaborMaterialItem[]>([]);
  const [excelCategories, setExcelCategories] = useState<string[]>([]);
  const [isLoadingExcel, setIsLoadingExcel] = useState(true);
  const [nycData, setNycData] = useState<NYCRenovationTier[]>([]);
  const [nycCategories, setNycCategories] = useState<string[]>([]);
  const [isLoadingNYC, setIsLoadingNYC] = useState(true);

  // Load Excel and NYC data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load Excel data
        const excelDataResult = await loadExcelData();
        setExcelData(excelDataResult.items);
        setExcelCategories(excelDataResult.categories);
        setIsLoadingExcel(false);
        
        // Load NYC cost data
        const nycDataResult = await loadNYCCostData();
        setNycData(nycDataResult.tiers);
        setNycCategories(nycDataResult.categories);
        setIsLoadingNYC(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setIsLoadingExcel(false);
        setIsLoadingNYC(false);
      }
    };
    
    loadData();
  }, []);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    
    if (!formData.type) newErrors.type = "Please select a renovation type";
    if (!formData.property) newErrors.property = "Please select a property type";
    if (!formData.zip) newErrors.zip = "Please enter a zip code";
    if (!formData.size) newErrors.size = "Please enter the area size";
    if (!formData.wallHeight) newErrors.wallHeight = "Please enter wall height";
    if (!formData.finish) newErrors.finish = "Please select a finish level";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateWallArea = (floorArea: number, wallHeight: number): number => {
    // Assuming a rectangular room, calculate wall area
    // For a square room: 4 walls * height * sqrt(area)
    const wallLength = Math.sqrt(floorArea) * 4; // Perimeter of the room
    return wallLength * wallHeight;
  };

  const estimateCosts = () => {
    if (!validateForm()) return;

    const size = Number(formData.size);
    const wallHeight = Number(formData.wallHeight);
    const finish = FINISH_LEVELS[formData.finish as keyof typeof FINISH_LEVELS];
    const type = RENOVATION_TYPES[formData.type as keyof typeof RENOVATION_TYPES];

    // Calculate areas
    const floorArea = size;
    const totalWallArea = calculateWallArea(size, wallHeight);

    // Calculate base cost using Excel data or fallback to hardcoded data
    let totalLaborCost = 0;
    let totalMaterialCost = 0;
    let totalBaseCost = 0;

    // Prioritize NYC data if available, then Excel data, then fallback to hardcoded data
    let costData: Record<string, { unit: string; price: number; laborPercent: number; materialPercent: number }>;
    
    if (nycData.length > 0) {
      // Use NYC cost data (most accurate for NYC area)
      costData = convertNYCToLaborUnitCosts({ tiers: nycData, categories: nycCategories, tierNames: [] });
      console.log(`Using NYC cost data with ${nycData.length} tiers`);
    } else if (excelData.length > 0) {
      // Fallback to Excel data
      costData = convertToLaborUnitCosts({ items: excelData, categories: excelCategories });
      console.log(`Using Excel data with ${excelData.length} items`);
    } else {
      // Fallback to hardcoded data
      costData = LABOR_UNIT_COSTS;
      console.log('Using hardcoded data');
    }

    // Filter items based on renovation type to avoid astronomical costs
    const relevantItems = Object.entries(costData).filter(([itemName, itemData]) => {
      const name = itemName.toLowerCase();
      
      // Filter based on renovation type
      if (formData.type === 'Kitchen') {
        return name.includes('kitchen') || name.includes('cabinet') || name.includes('millwork') || 
               name.includes('appliance') || name.includes('counter') || name.includes('plumbing') ||
               name.includes('electrical') || name.includes('demolition') || name.includes('floor') ||
               name.includes('paint') || name.includes('wall') || name.includes('ceiling');
      } else if (formData.type === 'Bathroom') {
        return name.includes('bathroom') || name.includes('plumbing') || name.includes('tile') ||
               name.includes('vanity') || name.includes('shower') || name.includes('toilet') ||
               name.includes('demolition') || name.includes('floor') || name.includes('paint') ||
               name.includes('wall') || name.includes('ceiling') || name.includes('electrical');
      } else if (formData.type === 'Flooring') {
        return name.includes('floor') || name.includes('carpet') || name.includes('tile') ||
               name.includes('wood') || name.includes('vinyl') || name.includes('demolition');
      } else if (formData.type === 'Painting') {
        return name.includes('paint') || name.includes('wall') || name.includes('ceiling') ||
               name.includes('trim') || name.includes('door') || name.includes('frame');
      } else if (formData.type === 'Whole Interior') {
        return name.includes('wall') || name.includes('ceiling') || name.includes('floor') ||
               name.includes('paint') || name.includes('demolition') || name.includes('electrical') ||
               name.includes('plumbing') || name.includes('millwork');
      } else {
        // For 'Other' type, include basic items
        return name.includes('wall') || name.includes('floor') || name.includes('paint') ||
               name.includes('demolition') || name.includes('electrical') || name.includes('plumbing');
      }
    });

    // Apply only relevant labor/unit costs
    relevantItems.forEach(([itemName, itemData]) => {
      let quantity = 0;
      
      // Calculate quantity based on unit type and renovation type
      if (itemData.unit === "SF") {
        if (itemName.includes("Wall") || itemName.includes("Partition") || itemName.includes("Ceiling")) {
          quantity = totalWallArea;
        } else {
          quantity = floorArea;
        }
      } else if (itemData.unit === "LF") {
        // Linear feet - estimate based on perimeter
        quantity = Math.sqrt(floorArea) * 4; // Perimeter
      } else if (itemData.unit === "EA") {
        // Each - estimate based on room size
        quantity = Math.max(1, Math.floor(floorArea / 100)); // 1 item per 100 sq ft
      } else if (itemData.unit === "SY") {
        // Square yards - convert from square feet
        quantity = floorArea / 9;
      } else {
        // Other units - use floor area as base
        quantity = floorArea / 100;
      }

      // Apply a reasonable multiplier to prevent astronomical costs
      const reasonableQuantity = Math.min(quantity, floorArea * 2); // Cap at 2x floor area
      const itemTotalCost = itemData.price * reasonableQuantity;
      const itemLaborCost = itemTotalCost * (itemData.laborPercent / 100);
      const itemMaterialCost = itemTotalCost * (itemData.materialPercent / 100);

      totalLaborCost += itemLaborCost;
      totalMaterialCost += itemMaterialCost;
      totalBaseCost += itemTotalCost;
    });

    // If no relevant items found, use a simplified calculation
    if (relevantItems.length === 0) {
      // Fallback to simple per-square-foot calculation
      const baseCostPerSF = finish.baseCostPerSF;
      totalBaseCost = floorArea * baseCostPerSF;
      totalLaborCost = totalBaseCost * type.laborRatio;
      totalMaterialCost = totalBaseCost * (1 - type.laborRatio);
    }

    // Add some debugging info
    console.log(`Using ${relevantItems.length} relevant items for ${formData.type} renovation`);
    console.log(`Base cost before adjustments: $${totalBaseCost.toLocaleString()}`);

    // Apply finish level multiplier
    const adjustedBaseCost = totalBaseCost * finish.multiplier;
    const adjustedLaborCost = totalLaborCost * finish.multiplier;
    const adjustedMaterialCost = totalMaterialCost * finish.multiplier;

    // Apply type-specific adjustments
    const finalBaseCost = adjustedBaseCost * type.baseMultiplier;
    const finalLaborCost = adjustedLaborCost * type.baseMultiplier;
    const finalMaterialCost = adjustedMaterialCost * type.baseMultiplier;

    // Scope adjustments
    const scopeAdjustments = formData.scope.reduce((total: number, item: string) => {
      const scopeItem = SCOPE_ITEMS[item as keyof typeof SCOPE_ITEMS];
      return total + (finalBaseCost * scopeItem.costMultiplier);
    }, 0);

    // Get location factor based on zip code and estimate type
    const locationData = getLocationFactor(formData.zip, formData.estimateType);
    const locationMultiplier = locationData.factor;

    // Get size scaling factor based on square footage
    const sizeScaleData = SIZE_SCALING_FACTORS.getScaleFactor(floorArea);
    const sizeScaleFactor = sizeScaleData.factor;

    console.log(`Size scaling factor: ${sizeScaleFactor} for ${floorArea} SF - ${sizeScaleData.description}`);

    // Calculate total costs using the location-specific low and high factors AND size scaling
    const totalLow = (finalBaseCost + scopeAdjustments) * locationData.lowFactor * sizeScaleFactor;
    const totalHigh = (finalBaseCost + scopeAdjustments) * locationData.highFactor * sizeScaleFactor;

    setCostBreakdown({
      baseCost: finalBaseCost,
      scopeAdjustments,
      locationMultiplier,
      locationRegion: locationData.region,
      locationDescription: locationData.description,
      locationLowFactor: locationData.lowFactor,
      locationHighFactor: locationData.highFactor,
      sizeScaleFactor: sizeScaleFactor,
      sizeScaleDescription: sizeScaleData.description,
      totalLow,
      totalHigh,
      wallArea: totalWallArea,
      floorArea: floorArea,
      laborCost: finalLaborCost,
      materialCost: finalMaterialCost
    });
  };

  const handleChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const toggleScope = (item: string) => {
    const updated = formData.scope.includes(item)
      ? formData.scope.filter(i => i !== item)
      : [...formData.scope, item];
    handleChange("scope", updated);
  };

  return (
    <div className="min-h-screen bg-white py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Main Content */}
        <div>
          {/* Top Disclaimer */}
          <div className="p-6 mb-8 flex justify-center">
            <div className="flex items-center max-w-4xl">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center">
                  <svg className="h-6 w-6 text-white" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-black mb-1">Professional Estimate Tool</h3>
                <p className="text-sm text-black">
                  This calculator provides estimates for educational purposes. Please verify costs with local contractors.
                </p>
              </div>
            </div>
          </div>

          {/* Header */}
          <div className="relative mb-12 flex flex-col items-center">
            {/* Logo in top right corner */}
            <div className="absolute top-0 right-0">
              <img 
                src="/Logo2.png" 
                alt="Company Logo" 
                className="h-16 w-auto opacity-90 hover:opacity-100 transition-opacity duration-200"
                onError={(e) => {
                  // Fallback to existing logo if custom logo not found
                  e.currentTarget.src = "/logo192.png";
                }}
              />
            </div>
            
            <div className="text-center max-w-4xl">
              <h1 className="text-6xl font-black text-black mb-4">
                Renovation Cost Estimator
              </h1>
              <p className="text-xl text-black mb-8">Get professional-grade estimates for your renovation project</p>
            </div>
            
            {(isLoadingExcel || isLoadingNYC) && (
              <div className="mt-6 p-6 flex justify-center">
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-black border-t-transparent mr-4"></div>
                  <span className="text-black text-lg font-medium">Loading professional pricing data...</span>
                </div>
              </div>
            )}
            {!isLoadingExcel && !isLoadingNYC && (excelData.length > 0 || nycData.length > 0) && (
              <div className="mt-6 p-6 flex justify-center">
                <div className="flex items-center justify-center">
                  <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center mr-4">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-black text-lg font-medium">
                    ✓ Loaded {nycData.length > 0 ? `${nycData.length} NYC cost tiers` : `${excelData.length} pricing items`}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div>
            {/* Progress Steps */}
            <div className="px-8 py-6">
              <div className="flex justify-center items-center">
                <div className="flex items-center space-x-4">
                  <div className="w-3 h-3 bg-black rounded-full"></div>
                  <div className="w-3 h-3 bg-black rounded-full"></div>
                  <div className="w-3 h-3 bg-black rounded-full"></div>
                </div>
              </div>
            </div>

            <div className="space-y-10">
              {/* Project Details */}
              <div className="space-y-8">
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-black mb-2">Project Details</h2>
                  <p className="text-black">Tell us about your renovation project</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-black mb-3">What are you renovating?</label>
                    <select
                      className={`w-full bg-transparent border-b-2 border-black text-black p-4 focus:ring-0 focus:border-black transition-all duration-200 ${
                        errors.type ? 'border-red-400' : 'hover:border-gray-500'
                      }`}
                      value={formData.type}
                      onChange={(e) => handleChange("type", e.target.value)}
                    >
                      <option value="" className="bg-white text-black">Select renovation type</option>
                      {Object.keys(RENOVATION_TYPES).map(type => (
                        <option key={type} value={type} className="bg-white text-black">{type}</option>
                      ))}
                    </select>
                    {errors.type && <p className="text-red-400 text-sm mt-2 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.type}
                    </p>}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-black mb-3">Property Type</label>
                    <select
                      className={`w-full bg-transparent border-b-2 border-black text-black p-4 focus:ring-0 focus:border-black transition-all duration-200 ${
                        errors.property ? 'border-red-400' : 'hover:border-gray-500'
                      }`}
                      value={formData.property}
                      onChange={(e) => handleChange("property", e.target.value)}
                    >
                      <option value="" className="bg-white text-black">Select property type</option>
                      <option value="Apartment" className="bg-white text-black">Apartment</option>
                      <option value="Condo" className="bg-white text-black">Condo</option>
                      <option value="Single-family home" className="bg-white text-black">Single-family home</option>
                      <option value="Multi-family" className="bg-white text-black">Multi-family</option>
                    </select>
                    {errors.property && <p className="text-red-400 text-sm mt-2 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.property}
                    </p>}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-black mb-3">Zip Code</label>
                    <input
                      type="text"
                      className={`w-full bg-white border-2 border-black text-black p-4 rounded-2xl focus:ring-4 focus:ring-black/50 focus:border-black transition-all duration-200 font-mono ${
                        errors.zip ? 'border-red-400' : 'hover:border-gray-500'
                      }`}
                      value={formData.zip}
                      onChange={(e) => handleChange("zip", e.target.value)}
                      placeholder="Enter zip code (e.g., 10001)"
                    />
                    {errors.zip && <p className="text-red-400 text-sm mt-2 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.zip}
                    </p>}
                    {formData.zip && formData.zip.length >= 5 && (
                      <div className="mt-3 p-2">
                        <div className="flex items-center mb-1">
                          <svg className="w-4 h-4 text-black mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-black font-medium text-sm">
                            {getLocationFactor(formData.zip, formData.estimateType).description}
                          </span>
                        </div>
                        <div className="text-xs text-black ml-6">
                          Factor: {getLocationFactor(formData.zip, formData.estimateType).factor}x 
                          (Range: {getLocationFactor(formData.zip, formData.estimateType).lowFactor}x - {getLocationFactor(formData.zip, formData.estimateType).highFactor}x)
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Specifications Section Header */}
              <div className="pt-10">
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-black mb-2">Specifications</h2>
                  <p className="text-black">Define your project requirements</p>
                </div>

                {/* New: Estimate Type Selector */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-black mb-1">Estimate Type</label>
                  <select
                    className="w-full bg-white border border-black text-black p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={formData.estimateType}
                    onChange={e => handleChange('estimateType', e.target.value)}
                  >
                    <option value="Low">Low</option>
                    <option value="Average">Average</option>
                    <option value="High">High</option>
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-black mb-1">Renovation Area (SF)</label>
                    <input
                      type="number"
                      className={`w-full bg-white border border-black text-black p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono ${
                        errors.size ? 'border-red-500' : 'border-black'
                      }`}
                      value={formData.size}
                      onChange={(e) => handleChange("size", e.target.value)}
                      placeholder="Enter square footage"
                    />
                    {errors.size && <p className="text-red-400 text-sm mt-1">{errors.size}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-black mb-1">Wall Height (feet)</label>
                    <input
                      type="number"
                      className={`w-full bg-white border border-black text-black p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono ${
                        errors.wallHeight ? 'border-red-500' : 'border-black'
                      }`}
                      value={formData.wallHeight}
                      onChange={(e) => handleChange("wallHeight", e.target.value)}
                      placeholder="Enter wall height"
                    />
                    {errors.wallHeight && <p className="text-red-400 text-sm mt-1">{errors.wallHeight}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-black mb-1">Finish Level</label>
                    <select
                      className={`w-full bg-white border border-black text-black p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.finish ? 'border-red-500' : 'border-black'
                      }`}
                      value={formData.finish}
                      onChange={(e) => handleChange("finish", e.target.value)}
                    >
                      <option value="">Select finish</option>
                      {Object.keys(FINISH_LEVELS).map(finish => (
                        <option key={finish} value={finish}>{finish}</option>
                      ))}
                    </select>
                    {errors.finish && <p className="text-red-400 text-sm mt-1">{errors.finish}</p>}
                  </div>
                </div>

                <div className="mt-6">
                  <label className="block text-sm font-medium text-black mb-2">Scope of Work</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(SCOPE_ITEMS).map(([item, details]) => (
                      <div
                        key={item}
                        className={`relative group ${
                          formData.scope.includes(item)
                            ? 'bg-white border-blue-500'
                            : 'border-black hover:bg-white'
                        } border rounded-lg p-3 cursor-pointer transition-colors`}
                        onClick={() => toggleScope(item)}
                      >
                        <div className="flex items-center text-left">
                          <input
                            type="checkbox"
                            checked={formData.scope.includes(item)}
                            onChange={() => {}}
                            className="rounded text-blue-500 focus:ring-blue-500 bg-white border-black"
                          />
                          <span className="ml-2 text-sm text-black text-left">{item}</span>
                        </div>
                        <div className="mt-1 text-xs text-black text-left">
                          {details.description}
                        </div>
                        <div className="absolute right-2 top-2 text-xs text-black">
                          +{(details.costMultiplier * 100).toFixed(0)}%
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Add cabinet linear feet input when Kitchen is selected */}
              {formData.type === 'Kitchen' && (
                <div className="p-6 border-t border-black">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-black mb-1">
                        Cabinet Linear Feet (Optional)
                      </label>
                      <input
                        type="number"
                        className="w-full bg-white border border-black text-black p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                        value={formData.cabinetLinearFeet}
                        onChange={(e) => handleChange("cabinetLinearFeet", e.target.value)}
                        placeholder="Enter cabinet linear feet"
                      />
                      <p className="text-sm text-black mt-1">
                        Leave blank to use calculated value based on room size
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Pricing Data Info */}
              {!isLoadingExcel && !isLoadingNYC && (excelData.length > 0 || nycData.length > 0) && (
                <div className="p-6 border-t border-black">
                  <h3 className="text-lg font-semibold text-black mb-4">Available Pricing Categories</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {(nycData.length > 0 ? nycCategories : excelCategories).map((category, index) => (
                      <div key={index} className="bg-white rounded p-2 text-sm text-black">
                        {category}
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-black mt-3">
                    Using {nycData.length > 0 ? 'NYC Home Renovation Cost Tiers' : 'Excel pricing data'} with {nycData.length > 0 ? nycData.length : excelData.length} items
                  </p>
                </div>
              )}

              {/* Calculate Button */}
              <div className="pt-8">
                <button
                  className="w-full bg-black hover:bg-black text-white px-8 py-6 font-bold text-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={estimateCosts}
                  disabled={isLoadingExcel || isLoadingNYC}
                >
                  <div className="flex items-center justify-center">
                    {(isLoadingExcel || isLoadingNYC) ? (
                      <>
                        <div className="animate-spin rounded-full h-6 w-6 border-4 border-white border-t-transparent mr-3"></div>
                        Loading Data...
                      </>
                    ) : (
                      <>
                        <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        Calculate Professional Estimate
                      </>
                    )}
                  </div>
                </button>
              </div>

              {/* Results */}
              {costBreakdown && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-black mb-2">
                      Professional Cost Estimate
                    </h2>
                    <p className="text-black">Detailed breakdown of your renovation costs</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center mr-3">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-black text-sm">Floor Area</p>
                          <p className="text-xl font-bold text-black font-mono">{costBreakdown.floorArea.toLocaleString()} SF</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-4">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center mr-3">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-black text-sm">Data Source</p>
                          <p className="text-lg font-semibold text-black">
                            {nycData.length > 0 ? 'NYC Cost Tiers' : excelData.length > 0 ? 'Excel Data' : 'Standard Data'}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center p-2 border-b border-black">
                      <span className="text-black">Wall Area</span>
                      <span className="font-mono font-medium text-black">{costBreakdown.wallArea.toLocaleString()} SF</span>
                    </div>
                    {formData.type === 'Kitchen' && (
                      <div className="flex justify-between items-center p-2 border-b border-black">
                        <span className="text-black">IKEA Cabinetry (Linear Ft)</span>
                        <span className="font-mono font-medium text-black">
                          {formData.cabinetLinearFeet 
                            ? formData.cabinetLinearFeet 
                            : Math.sqrt(Number(formData.size)) * 2} LF
                        </span>
                      </div>
                    )}
                      <div className="flex justify-between items-center p-2 border-b border-black">
                        <span className="text-black">Base Cost</span>
                      <span className="font-mono font-medium text-black">${costBreakdown.baseCost.toLocaleString()}</span>
                    </div>
                      <div className="flex justify-between items-center p-2 border-b border-black">
                        <span className="text-black">Labor Cost</span>
                      <span className="font-mono font-medium text-black">${costBreakdown.laborCost.toLocaleString()}</span>
                    </div>
                      <div className="flex justify-between items-center p-2 border-b border-black">
                        <span className="text-black">Material Cost</span>
                      <span className="font-mono font-medium text-black">${costBreakdown.materialCost.toLocaleString()}</span>
                    </div>
                      <div className="flex justify-between items-center p-2 border-b border-black">
                        <span className="text-black">Scope Adjustments</span>
                      <span className="font-mono font-medium text-black">${costBreakdown.scopeAdjustments.toLocaleString()}</span>
                    </div>
                      <div className="flex justify-between items-center p-2 border-b border-black">
                        <span className="text-black">Location Factor (Current)</span>
                      <span className="font-mono font-medium text-black">x{costBreakdown.locationMultiplier}</span>
                    </div>
                      <div className="flex justify-between items-center p-2 border-b border-black">
                        <span className="text-black">Region</span>
                      <span className="font-mono font-medium text-black">{costBreakdown.locationRegion}</span>
                    </div>
                      <div className="flex justify-between items-center p-2 border-b border-black">
                        <span className="text-black">Area Description</span>
                      <span className="font-mono font-medium text-black">{costBreakdown.locationDescription}</span>
                    </div>
                      <div className="flex justify-between items-center p-2 border-b border-black">
                        <span className="text-black">Location Factor Range</span>
                      <span className="font-mono font-medium text-black">
                        {costBreakdown.locationLowFactor}x - {costBreakdown.locationHighFactor}x
                      </span>
                    </div>
                      <div className="flex justify-between items-center p-2 border-b border-black">
                        <span className="text-black">Project Size Factor</span>
                      <span className="font-mono font-medium text-black">x{costBreakdown.sizeScaleFactor.toFixed(3)}</span>
                    </div>
                      <div className="flex justify-between items-center p-2 border-b border-black">
                        <span className="text-black">Size Description</span>
                      <span className="font-mono text-sm text-black text-right max-w-xs">{costBreakdown.sizeScaleDescription}</span>
                    </div>
                    <div className="mt-8 pt-8 border-t-2 border-black">
                      <div className="text-center">
                        <div className="mb-4">
                          <h3 className="text-2xl font-bold text-black mb-2">Total Project Estimate</h3>
                          <p className="text-black">Professional renovation cost range</p>
                        </div>
                        <div className="text-5xl font-black text-black mb-4">
                          ${costBreakdown.totalLow.toLocaleString()}
                        </div>
                        <div className="text-2xl text-black mb-2">to</div>
                        <div className="text-5xl font-black text-black mb-6">
                          ${costBreakdown.totalHigh.toLocaleString()}
                        </div>
                        <div className="text-sm text-black">
                          Based on {formData.type} renovation • {formData.finish} finish level
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 